import {
  AsyncPipe,
  CurrencyPipe,
  DatePipe,
  KeyValuePipe,
  NgComponentOutlet,
  NgTemplateOutlet,
} from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Type,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { PartnerLogoService } from 'src/app/shared/services/partner-logo/partner-logo.service';
import { WorkflowService } from 'src/app/shared/services/workflow/workflow.service';
import { claimSumRemainingAmount } from 'src/types/claim-sum';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { CountryFlagPipe } from '../../pipes/country-flag/country-flag.pipe';
import { CaseDetailsComponent } from '../case-details/case-details.component';
import { CaseEventsComponent } from '../case-events/case-events.component';
import {
  AdditionalAction,
  CasesActionsMenuComponent,
} from '../cases-actions-menu/cases-actions-menu.component';
import {
  CivilLawsuitContactDialogComponent,
  CivilLawsuitContactDialogData,
} from '../civil-lawsuit-contact-dialog/civil-lawsuit-contact-dialog.component';
import { CofaceCasesService } from '../coface-cases.service';
import {
  DialogData,
  DownloadCaseDocumentsDialogComponent,
} from '../download-case-documents-dialog/download-case-documents-dialog.component';
import { PayeeCasesService } from '../payee-cases.service';
import { PulseSyncDirective } from '../pulse-sync.directive';
import {
  StartPaymentChoiceDialogComponent,
  StartPaymentChoiceDialogData,
} from '../start-payment-choice-dialog/start-payment-choice-dialog.component';
import {
  CaseTableItem,
  CaseTableItemAction,
  Pagination,
  UNKNOWN_CLIENT_NAME,
} from '../types';

export type { AdditionalAction };

export type AdditionalColumn = {
  columnDef: string;
  sortable: string | null;
  header: Observable<string>;
  cell: Type<any>;
};

@Component({
  selector: 'payee-cases-table',
  templateUrl: './cases-table.component.html',
  styleUrls: ['./cases-table.component.scss'],
  providers: [MatTable, DatePipe],
  imports: [
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    NgComponentOutlet,
    PayeeIconComponent,
    MatProgressSpinner,
    MatTooltip,
    PayeeIconButtonComponent,
    CasesActionsMenuComponent,
    MatIcon,
    CaseEventsComponent,
    CaseDetailsComponent,
    PulseSyncDirective,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    AsyncPipe,
    CurrencyPipe,
    DatePipe,
    KeyValuePipe,
    CountryFlagPipe,
    TranslateModule,
    NgTemplateOutlet,
  ],
})
export class CasesTableComponent implements OnInit, OnDestroy {
  private partnerLogoService = inject(PartnerLogoService);
  private workflowService = inject(WorkflowService);
  private snackbar = inject(MatSnackBar);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private datePipe = inject(DatePipe);
  private payeeCasesService = inject(PayeeCasesService);
  readonly cofaceCasesService = inject(CofaceCasesService);
  readonly now = signal(Date.now());
  private countdownTimer?: ReturnType<typeof setInterval>;

  private readonly desktopColumns = [
    'payee_case_reference_id',
    'started_at',
    'parties',
    'client',
    'state',
    'partner',
    'original_claim',
    'payment_status',
    'actions',
  ];
  private readonly mobileColumns = ['id_multi', 'debt_multi', 'actions'];

  @Input() allowEdit = true;
  @Input() allowDelete = true;
  readonly showClientColumn = input(true);
  readonly showPartnerColumn = input(true);

  readonly dataSource = input.required<MatTableDataSource<CaseTableItem>>();
  @Input() pagination?: Pagination;
  readonly additionalColumns = input<AdditionalColumn[]>([]);
  @Input() additionalActions: AdditionalAction[] = [];

  @Output() page = new EventEmitter<PageEvent>();

  private readonly isMobile = signal(
    typeof window !== 'undefined' &&
    (window.innerWidth <= 768 || screen.width <= 768),
  );
  private readonly clientFilter = toSignal(this.payeeCasesService.client);

  readonly displayedColumns = computed(() => {
    const columns = this.isMobile() ? this.mobileColumns : this.desktopColumns;
    const showClient = this.showClientColumn() && !this.clientFilter();

    return columns
      .slice(0, columns.length - 1)
      .filter((column) => column !== 'client' || showClient)
      .filter((column) => column !== 'partner' || this.showPartnerColumn())
      .concat(this.additionalColumns().map((c) => c.columnDef))
      .concat(['actions']);
  });

  private lockExpandedElement = false;
  readonly expandedElement = signal<CaseTableItem | null>(null);

  constructor() {
    effect(() => {
      const data = this.dataSource().data;

      this.expandedElement.set(null);
      if (data.length === 1) {
        this.expandedElement.set(data[0]);
      }
    });
  }

  @HostListener('window:resize') setIsMobile(): void {
    this.isMobile.set(window.innerWidth <= 768 || screen.width <= 768);
  }

  @ViewChild(MatSort) set sort(sort: MatSort) {
    const sortBy = this.payeeCasesService.sort_by.value;
    if (sortBy && sort.active !== sortBy) {
      setTimeout(() => {
        sort.sort({
          disableClear: false,
          id: sortBy,
          start: this.payeeCasesService.sort_direction.value,
        });
      });
    }
  }

  get getPartnerLogoName() {
    return this.partnerLogoService.getPartnerMiniLogoName;
  }

  ngOnInit(): void {
    this.countdownTimer = setInterval(() => {
      this.now.set(Date.now());
    }, 30_000);
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  onSortChange(sort: Sort): void {
    this.payeeCasesService.sort_by.next(sort.direction ? sort.active : null);
    this.payeeCasesService.sort_direction.next(sort.direction);
  }

  onActionsMenuOpen(): void {
    this.lockExpandedElement = true;
  }

  onActionsMenuClosed(): void {
    this.lockExpandedElement = false;
  }

  filterHistoricCase(element: CaseTableItem): void {
    this.payeeCasesService.payee_case_reference_id.next(
      element.payee_case_reference_id,
    );
  }

  updateExpandedElement(element: CaseTableItem): void {
    if (this.lockExpandedElement) {
      return;
    }

    if (this.expandedElement() === element) {
      this.expandedElement.set(null);
    } else {
      this.expandedElement.set(element);
    }
  }

  getDisplayedClaimCurrencyCount(element: CaseTableItem): number {
    return Object.keys(element.displayed_claim).length;
  }

  getHiddenClaimOriginalAmount(
    element: CaseTableItem,
    currency: string,
  ): number {
    return element.hidden_claim?.[currency]?.original_amount ?? 0;
  }

  getPaymentStatus(
    element: CaseTableItem,
  ):
    | 'none'
    | 'partial'
    | 'full'
    | 'partial-dispute'
    | 'full-dispute'
    | 'partial-and-dispute' {
    const sums = [
      ...Object.values(element.displayed_claim),
      ...Object.values(element.hidden_claim),
    ];

    let anyPaid = false;
    let anyRemaining = false;
    let anyDisputed = false;

    for (const sum of sums) {
      if (sum.disputed_amount > 0) {
        anyDisputed = true;
      }

      if (sum.paid_amount > 0) {
        anyPaid = true;
      }

      if (claimSumRemainingAmount(sum) > 0) {
        anyRemaining = true;
      }
    }

    if (anyPaid) {
      if (!anyRemaining) {
        return 'full';
      }
      if (anyDisputed) {
        return 'partial-and-dispute';
      }
      return 'partial';
    }

    if (anyDisputed) {
      if (!anyRemaining) {
        return 'full-dispute';
      }
      return 'partial-dispute';
    }

    return 'none';
  }

  getStatusChipTooltip(element: CaseTableItem, fallbackKey?: string): string {
    const expected = element.expected_transition;
    if (!expected) {
      return fallbackKey ? this.translate.instant(fallbackKey) : '';
    }

    const date = this.datePipe.transform(expected.at, 'yyyy. MM. dd.') ?? '';
    const overdue = new Date(expected.at).getTime() <= this.now();
    const deadline = this.translate.instant(
      overdue
        ? 'CASES_TABLE.TOOLTIP.DEADLINE_OVERDUE'
        : 'CASES_TABLE.TOOLTIP.DEADLINE',
      { date },
    );
    const nextEvent = this.translate.instant(
      'CASES_TABLE.TOOLTIP.EXPECTED_EVENT',
      { event: expected.label },
    );

    return `${deadline}\n\n${nextEvent}`;
  }

  getExpectedTransitionCountdown(at: string, now: number): string {
    const remainingMs = new Date(at).getTime() - now;
    if (remainingMs <= 0) {
      return this.translate.instant('CASES_TABLE.COUNTDOWN_OVERDUE');
    }

    const totalMinutes = Math.floor(remainingMs / 60_000);
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;

    const time =
      days > 0
        ? this.translate.instant('CASES_TABLE.COUNTDOWN_WITH_DAYS', {
          days,
          hours,
          minutes,
        })
        : this.translate.instant('CASES_TABLE.COUNTDOWN', {
          hours,
          minutes,
        });

    return `${this.translate.instant('CASES_TABLE.COUNTDOWN_UNTIL')} ${time}`;
  }

  getDebtorDisplayName(element: CaseTableItem): string {
    const name = element.parties.debtor_name ?? '';
    if (name.length <= 25) {
      return name;
    }

    return `${name.slice(0, 25).trimEnd()}...`;
  }

  getDebtorTooltip(element: CaseTableItem): string {
    const parts: string[] = [];
    const debtorName = element.parties.debtor_name || '';

    if (element.parties.debtor_address) {
      const addressLabel = this.translate.instant(
        'CASES_TABLE.TOOLTIP.ADDRESS_LABEL',
      );
      parts.push(`${addressLabel}: ${element.parties.debtor_address}`);
    }

    if (element.parties.debtor_tax_number) {
      const taxNumberLabel = this.translate.instant(
        'CASES_TABLE.TOOLTIP.TAX_NUMBER_LABEL',
      );
      parts.push(`${taxNumberLabel}: ${element.parties.debtor_tax_number}`);
    }

    if (parts.length > 0) {
      return `${debtorName}\n${parts.join('\n')}`;
    }

    if (debtorName.length > 25) {
      return debtorName;
    }

    return '';
  }

  async copyCaseIdToClipboard(
    caseId: string,
    event: MouseEvent,
    _element: CaseTableItem,
  ): Promise<void> {
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(caseId);

      this.snackbar.open(
        this.translate.instant('CASES_TABLE.COPY_SUCCESS'),
        this.translate.instant('OK'),
        { duration: 2000 },
      );
    } catch (error) {
      console.error('Failed to copy to clipboard', error);
    }
  }

  editCase(element: CaseTableItem): void {
    this.router.navigateByUrl(`/case/${element.id}`);
  }

  editCaseFromChip(element: CaseTableItem, event: MouseEvent): void {
    event.stopPropagation();
    this.editCase(element);
  }

  openStartPaymentChoice(element: CaseTableItem, event: MouseEvent): void {
    event.stopPropagation();
    this.dialog.open<StartPaymentChoiceDialogComponent, StartPaymentChoiceDialogData>(
      StartPaymentChoiceDialogComponent,
      {
        data: {
          caseId: element.id,
          canPayWithCredit: element.can_pay_with_credit,
        },
        autoFocus: false,
      },
    );
  }

  openDownloadDocumentsDialog(element: CaseTableItem): void {
    this.dialog.open<DownloadCaseDocumentsDialogComponent, DialogData>(
      DownloadCaseDocumentsDialogComponent,
      {
        data: {
          case_uuid: element.id,
          payee_case_reference_id: element.payee_case_reference_id,
        },
      },
    );
  }

  clickableStateAction(element: CaseTableItem): CaseTableItemAction | null {
    const action = element.state.action;
    return action?.dialog ? action : null;
  }

  openStateAction(element: CaseTableItem, event: MouseEvent): void {
    event.stopPropagation();

    const action = element.state.action;
    if (!action?.dialog) {
      return;
    }

    this.workflowService.openTransitionDialog({
      transitionId: action.id,
      caseId: element.id,
      dialog: action.dialog,
    });
  }

  stateActionTooltip(element: CaseTableItem): string {
    const actionLabel = element.state.action?.label ?? '';
    const expected = this.getStatusChipTooltip(element);

    return [actionLabel, expected].filter(Boolean).join('\n\n');
  }

  openCivilLawsuitDialog(element: CaseTableItem, event: MouseEvent): void {
    event.stopPropagation();

    if (!element.civil_lawsuit_contact) {
      return;
    }

    this.dialog.open<
      CivilLawsuitContactDialogComponent,
      CivilLawsuitContactDialogData
    >(CivilLawsuitContactDialogComponent, {
      data: {
        contact: element.civil_lawsuit_contact,
        payee_case_reference_id: element.payee_case_reference_id,
        debtor_name: element.parties.debtor_name ?? '',
      },
    });
  }

  debtorTooltipPressed(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  filterByClient(element: CaseTableItem, event: MouseEvent): void {
    event.stopPropagation();

    if (!element.client_id) {
      return;
    }

    this.payeeCasesService.client.next({
      uuid: element.client_id,
      name: element.parties.client_name || UNKNOWN_CLIENT_NAME,
    });
  }
}
